import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'

// Device identity for the registration / approval gate.
//
// ── Why not a MAC address ────────────────────────────────────────────────────
// A MAC cannot be read on either mobile platform any more:
//   iOS 7+      — every app gets the constant 02:00:00:00:00:00
//   Android 6+  — same constant; Android 10+ also blocks NetworkInterface
// So a MAC is neither unique nor obtainable. The vendor-sanctioned stand-ins
// are what we use, and they are what the backend's `devices` table already
// holds for its existing Android rows:
//   android — Settings.Secure.ANDROID_ID   (Device.getId())
//   ios     — identifierForVendor / IDFV   (Device.getId())
//   web     — a random UUID we mint once and keep in localStorage
//
// ── Why the web id is not the IP ─────────────────────────────────────────────
// A browser cannot read its own IP, and an IP is a poor identity anyway: an
// office LAN NATs every PC behind one address, and DHCP reassigns it. The
// server records the IP from the request itself (see the Laravel controller);
// the localStorage UUID is what actually identifies the browser.

const DEVICE_UUID_KEY = 'popin_device_uuid'

// Deliberately NOT part of storage.clearAll() — signing out must not hand the
// user a brand-new device identity that needs re-approval.
function getOrCreateWebUuid() {
  let uuid = localStorage.getItem(DEVICE_UUID_KEY)
  if (uuid) return uuid

  uuid = generateUuid()
  localStorage.setItem(DEVICE_UUID_KEY, uuid)
  return uuid
}

// crypto.randomUUID needs a secure context and isn't in older WebViews;
// getRandomValues is far more widely available, with a last-resort fallback.
function generateUuid() {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()

    if (typeof crypto.getRandomValues === 'function') {
      const bytes = crypto.getRandomValues(new Uint8Array(16))
      bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
      bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
    }
  }
  return `web-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`
}

/**
 * Collect the identity payload for POST /api/device/register.
 * Shape mirrors the `devices` table columns exactly.
 *
 * @returns {Promise<{uuid:string, platform:string, version:string, manufacturer:string, model:string}>}
 */
export async function getDeviceIdentity() {
  const native = Capacitor.isNativePlatform()

  if (!native) {
    return {
      uuid: getOrCreateWebUuid(),
      platform: 'web',
      version: navigator.userAgent || '',
      manufacturer: browserName(),
      model: navigator.platform || 'Browser',
    }
  }

  const [id, info] = await Promise.all([Device.getId(), Device.getInfo()])

  // Capacitor renamed this field: `identifier` from v4 on, `uuid` before that.
  // Accept either so the app isn't tied to one plugin major.
  const identifier = id?.identifier || id?.uuid

  if (!identifier) {
    throw new Error('Device identifier unavailable')
  }

  return {
    uuid: identifier,
    platform: info?.platform || Capacitor.getPlatform(), // 'ios' | 'android'
    version: info?.osVersion || '',
    manufacturer: info?.manufacturer || '',
    model: info?.model || '',
  }
}

/** Best-effort browser label, used only as the `manufacturer` display value. */
function browserName() {
  const ua = navigator.userAgent || ''
  if (/Edg\//.test(ua)) return 'Microsoft Edge'
  if (/OPR\/|Opera/.test(ua)) return 'Opera'
  if (/Chrome\//.test(ua)) return 'Google Chrome'
  if (/Firefox\//.test(ua)) return 'Mozilla Firefox'
  if (/Safari\//.test(ua)) return 'Safari'
  return 'Browser'
}

/** Exposed for support: lets someone read their device id off the screen. */
export function getStoredWebUuid() {
  return localStorage.getItem(DEVICE_UUID_KEY)
}
